import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentSingleTabManager } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: (process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '').trim(),
  authDomain: (process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || '').trim(),
  projectId: (process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '').trim(),
  storageBucket: (process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || '').trim(),
  messagingSenderId: (process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '').trim(),
  appId: (process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '').trim(),
  measurementId: (process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || '').trim()
};

// Only initialize Firebase if we have valid configuration
// During build time, if no API key is provided, use a dummy configuration
const isDummyConfig = !firebaseConfig.apiKey || firebaseConfig.apiKey === '';
const configToUse = isDummyConfig ? {
  apiKey: 'dummy-api-key-for-build',
  authDomain: 'dummy.firebaseapp.com',
  projectId: 'dummy-project',
  storageBucket: 'dummy.appspot.com',
  messagingSenderId: '123456789',
  appId: '1:123456789:web:abc123'
} : firebaseConfig;

// Initialize Firebase app
const app = getApps().length === 0 ? initializeApp(configToUse) : getApp();

// Initialize Firestore with offline persistence for better performance
// This enables local caching and reduces network requests significantly
function initializeDb() {
  try {
    if (getApps().length === 1) {
      // Only initialize if this is the first app initialization
      // persistentSingleTabManager (forceOwnership: false) gives offline
      // persistence without the multi-tab coordination that's been throwing
      // "INTERNAL ASSERTION FAILED (ID b815 / ca9)" during dev hot reloads.
      return initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentSingleTabManager({ forceOwnership: false }),
        }),
      });
    } else {
      // If app already exists, get existing Firestore instance
      return getFirestore(app);
    }
  } catch (error) {
    // Fallback to default Firestore if persistence fails (e.g., in private browsing)
    console.warn('Firestore persistence failed, using default cache:', error);
    return getFirestore(app);
  }
}

// Export services
export const auth = getAuth(app);
export const db = initializeDb();
export const storage = getStorage(app);
export const functions = getFunctions(app);

export const initAnalytics = async () => {
  if (typeof window !== 'undefined' && await isSupported()) {
    return getAnalytics(app);
  }
  return null;
};

export default app;